<template>
    <div :style="'border-left: ' + marginIndentation + 'px solid ' + marginChildColor">
        <div
            :id="`contract-${item.id}`"
            class="contract-list-item"
            :class="[item.id === contractFilterExists ? 'contracts-list-contract-filter-exists' : '', lineClass]"
        >
            <div class="main">
                <div class="meta">
                    <DIcon
                        v-c-tooltip="statusTooltip"
                        class="pointer-events"
                        :name="statusIcon"
                    />
                    <div>
                        <span v-if="item.display_ff_contract_value">
                            #{{ item.id }}<template v-if="item.display_ff_contract_value"> - {{ item.display_ff_contract_value }}</template>
                        </span>
                        <span v-else>
                            #{{ item.id }}<template v-if="item.display_value"> - {{ item.display_value }}</template>
                        </span>
                        <span>
                            {{ quantity(item) }} x {{ item.product_name }}
                        </span>
                    </div>
                </div>
                <div class="states">
                    <DTag
                        :label="`${item.status} ${suspendReason} ${terminatedReason}`"
                        color="primary"
                    />
                    <DTag
                        v-if="item.fulfillment_module === 'externaldomain'"
                        :label="__('customers.contracts.external_domain')"
                    />
                    <DTag
                        v-if="item.one_time"
                        :label="__('customers.contracts.one_time')"
                    />
                    <DTag
                        v-if="item.current_contract_phase && (item.current_contract_phase.price == 0.000 || item.current_contract_phase.discounted_price == 0.000) && item.is_ff_child_contract === false"
                        :label="__('customers.contracts.inclusive')"
                        color="success"
                    />
                    <DTag
                        v-if="item.current_contract_phase && item.is_addon && item.is_ff_child_contract === false"
                        :label="__('customers.contracts.addon')"
                        color="secondary"
                    />
                    <DTag
                        v-if="item.current_contract_phase && (item.current_contract_phase.price == 0.000 || item.current_contract_phase.discounted_price == 0.000) && item.is_ff_child_contract === true"
                        :label="__('customers.contracts.fulfillment')"
                        color="purple"
                    />
                    <DTag
                        v-if="item.relation_type && item.relation_reference"
                        :label="item.relation_type+': '+item.relation_reference"
                        color="info"
                    />
                    <DTag
                        v-if="!isChild && item.transfer && item.transfer.status === 'open'"
                        label="in transfer"
                        color="primary"
                    />
                </div>
                <div class="actions">
                    <SsoButtons
                        v-if="item.hasSSO && item.status !== 'draft'"
                        :fulfillment-account="item.fulfillment_account"
                        :sso-types="item.ssoTypes"
                        @navigateToSSO="navigateToSSO(item, $event)"
                    />
                    <DButton
                        class="pointer-events"
                        icon="eye"
                        :href="showCustomerContractLink(item)"
                    />
                    <ContractListItemDropdown
                        class="pointer-events"
                        :item="item"
                        :parent-contract-status="parentContract === null ? null : parentContract.status"
                        @clickActivate="showActivateContractModal"
                        @clickTerminate="clickTerminate"
                        @clickTerminateSilently="clickTerminateSilently"
                        @clickCancelTerminate="clickCancelTerminate"
                        @clickApplyMoneyBackGuarantee="clickApplyMoneyBackGuarantee"
                        @clickWithdrawal="clickWithdrawal"
                        @clickMoveContract="clickMoveContract"
                        @clickCancel="clickCancelContract"
                        @clickUnsuspend="clickUnsuspend"
                        @clickRelease="clickRelease"
                        @clickReleaseTree="clickReleaseTree"
                        @clickSendAuthCode="clickSendAuthCode"
                        @clickDelete="clickDelete"
                        @clickDeleteTree="clickDeleteTree"
                        @clickRestore="clickRestore"
                        @clickAddAddon="clickAddAddon"
                    />
                </div>
            </div>

            <div class="middle">
                <DIcon
                    v-c-tooltip="'<div class=\'loading-tooltip-body\'>'+__('messages.loading')+'</div>'"
                    name="cog"
                    class="pointer-events"
                    @mouseover="setTooltipContents(item)"
                />
            </div>

            <div class="secondary">
                <div class="price">
                    <DIcon
                        v-c-tooltip="moneyTooltip"
                        name="cash"
                        class="pointer-events"
                    />
                    <div>
                        <div v-if="item.one_time">
                            <div><strong>{{ __('contracts.list.price') }}</strong> {{ netPrice(item) }} / {{ grossPrice(item) }} <span v-if="item.is_volume_based_module === true">({{ totalNetPrice(item) }} / {{ totalGrossPrice(item) }})</span></div>
                        </div>
                        <div v-else-if="item.current_contract_phase && item.current_contract_phase.discounted_price != 0.000">
                            <div><strong>{{ __('contracts.list.price') }}</strong> {{ netPrice(item) }} / {{ grossPrice(item) }} <span v-if="item.is_volume_based_module === true">({{ totalNetPrice(item) }} / {{ totalGrossPrice(item) }})</span></div>
                            <div><strong>{{ __('contracts.list.billingcycle') }}</strong> {{ billingCycle(item.current_contract_phase.billing_cycle) }}</div>
                        </div>
                    </div>
                </div>
                <div class="contract-term">
                    <DIcon
                        v-c-tooltip="__('contracts.list.tooltips.terms')"
                        name="file-document-edit-outline"
                        class="pointer-events"
                    />
                    <div>
                        <div v-if="!item.current_contract_phase">
                            <DTag
                                color="danger"
                                :label="__('customers.contracts.no_contract_phases')"
                            />
                        </div>
                        <div v-if="item.current_contract_phase && item.one_time === false">
                            <div><strong>{{ __('contracts.list.term') }}</strong> {{ item.current_contract_phase.contract_term }} {{ __('messages.contract_term_units.' + item.current_contract_phase.contract_term_unit) }}</div>
                            <div><strong>{{ __('contracts.list.notice') }}</strong> {{ item.current_contract_phase.notice_period }} {{ __('messages.contract_term_units.' + item.current_contract_phase.notice_period_unit) }}</div>
                        </div>
                        <div v-else>
                            <DTag
                                color="danger"
                                :label="__('customers.contracts.no_contract_phases')"
                            />
                        </div>
                    </div>
                </div>
                <div class="contract-start">
                    <DIcon
                        v-c-tooltip="__('contracts.list.tooltips.start')"
                        name="clock-outline"
                        class="pointer-events"
                    />
                    <div>
                        <div>
                            <strong>{{ __('contracts.list.start') }}</strong> {{ setDateFormatEuropeBerlin(item.start_date) }}
                            <span v-if="item.end_date !== null">
                                / <strong>{{ __('contracts.list.end') }}</strong> {{ setDateFormatEuropeBerlin(item.end_date) }}
                            </span>
                        </div>
                        <div v-if="item.billed_until_date">
                            <strong>{{ __('contracts.list.billed') }}</strong> {{ setDateFormatEuropeBerlin(item.billed_until_date) }}
                        </div>
                        <div v-else>
                            <strong>{{ __('contracts.list.billed') }}</strong>
                            <DTag
                                :label="__('customers.contracts.no_billing')"
                            />
                        </div>
                    </div>
                </div>
                <div class="connections">
                    <DIcon
                        v-if="connections.length > 0"
                        v-c-tooltip="__('customers.contracts.connections.tooltip')"
                        name="transit-connection-variant"
                        class="pointer-events"
                    />
                    <div>
                        <div
                            v-for="connection in connections"
                            :key="`${connection.connection_type}-${connection.id}`"
                        >
                            <DTag :label="__(`customers.contracts.connections.types.${connection.connection_type}`)" />
                            <DTag
                                :label="connection.status"
                                color="primary"
                            />
                            <a
                                class="pointer-events"
                                href="#"
                                :title="connectionLabel(connection)"
                                @click.prevent="focusConnection(connection)"
                            >
                                {{ connectionLabel(connection) }}
                            </a>
                        </div>
                    </div>
                </div>
                <div class="notes">
                    <IncidentFlag
                        v-if="userPermissions.includes('customer.incidents_view') && item.has_incidents"
                        id="contracts-incident-flag"
                        :active-incident="item.has_open_incidents"
                        :relation-type="'contracts'"
                        :relation-id="item.id"
                        :customer-id="item.customer_id"
                    />
                    <DButton
                        v-if="item.notes_internal"
                        class="pointer-events"
                        icon="note-alert"
                        :label="__('contracts.list.internalNote')"
                        @click="showInternalNoteModal = true"
                    />
                </div>
                <div
                    v-if="typeof item.notes_invoice === 'string' && item.notes_invoice.length !== 0"
                    class="invoice-note"
                >
                    <DIcon
                        v-c-tooltip="__('contracts.list.invoiceNote')"
                        name="note-alert"
                        class="pointer-events"
                    />
                    <div>
                        <p>{{ invoiceNoteExcerpt.excerpt }}<span v-if="invoiceNoteExcerpt.hasMore">&mldr;</span></p>

                        <DButton
                            v-if="invoiceNoteExcerpt.hasMore"
                            :label="__('messages.show_more')"
                            icon="eye"
                            class="pointer-events"
                            @click="showInvoiceNoteModal = true"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="children pointer-events">
            <ContractListItem
                v-for="childItem in item.children"
                :key="childItem.id"
                :item="childItem"
                :contract-filter-exists="contractFilterExists"
                :is-child="childItem.parent_contract_id"
                :parent-contract="childItem.parent_contract_id !== null ? item : null"
                :customer="customer"
                @navigateToSSO="navigateToSSO"
                @clickActivate="showActivateContractModal"
                @clickTerminate="clickTerminate"
                @clickTerminateSilently="clickTerminateSilently"
                @clickCancelTerminate="clickCancelTerminate"
                @clickApplyMoneyBackGuarantee="clickApplyMoneyBackGuarantee"
                @clickWithdrawal="clickWithdrawal"
                @clickMoveContract="clickMoveContract"
                @clickCancelContract="clickCancelContract"
                @clickUnsuspend="clickUnsuspend"
                @clickRelease="clickRelease"
                @clickReleaseTree="clickReleaseTree"
                @clickSendAuthCode="clickSendAuthCode"
                @clickDelete="clickDelete"
                @clickDeleteTree="clickDeleteTree"
                @clickRestore="clickRestore"
                @clickAddAddon="clickAddAddon"
            />
        </div>

        <DModal
            v-model:show="showInternalNoteModal"
            :title="__('contracts.list.internalNoteTitle') + item.id"
            :message="item.notes_internal"
            @close="showInternalNoteModal = false"
        />

        <DModal
            v-model:show="showInvoiceNoteModal"
            :title="__('contracts.list.invoiceNoteTitle') + item.id"
            :message="item.notes_internal"
            @close="showInvoiceNoteModal = false"
        >
            <template #default>
                <div class="invoice-note-modal-text">
                    <p
                        v-for="(note_invoice_paragraph, index) in nl2array(item.notes_invoice)"
                        :key="index"
                    >
                        {{ note_invoice_paragraph }}
                    </p>
                </div>
            </template>
        </DModal>
    </div>
</template>

<script>
import ContractListItemDropdown from '~@/Components/Customers/Contracts/ContractListItemDropdown'
import IncidentFlag from '~@/Components/Customers/BusinessIncidents/IncidentFlag'
import billingCycle from '~@/Mixins/billingCycle'
import nl2array from '~@/Mixins/nl2array'
import priceFormat from '~@/Mixins/priceFormat'

import SsoButtons from '~@/Components/Customers/Contracts/SsoButtons'

const FOCUS_HIGHLIGHT_MS = 2000

export default {
    name: 'ContractListItem',
    components: {
        ContractListItemDropdown,
        IncidentFlag,
        SsoButtons
    },
    mixins: [
        billingCycle,
        nl2array,
        priceFormat
    ],
    props: {
        item: {
            type: Object,
            default: () => {
                return {}
            }
        },
        parentContract: {
            type: Object,
            default: () => {
                return null
            }
        },
        contractFilterExists: {
            type: Number,
            default: () => {
                return 0
            }
        },
        isChild: {
            type: [Number, Boolean],
            default: () => {
                return false
            }
        },
        customer: {
            type: Object,
            default: () => {
                return {}
            }
        }
    },
    emits: [
        'navigateToSSO', 'clickActivate', 'clickTerminate', 'clickTerminateSilently',
        'clickCancelTerminate', 'clickApplyMoneyBackGuarantee', 'clickWithdrawal', 'clickMoveContract',
        'clickCancelContract', 'clickUnsuspend', 'clickRelease', 'clickReleaseTree', 'clickSendAuthCode',
        'clickDelete', 'clickDeleteTree', 'clickRestore', 'clickAddAddon'
    ],
    data () {
        return {
            showInternalNoteModal: false,
            showInvoiceNoteModal: false,
            depth: 0
        }
    },
    computed: {
        lineClass () {
            const lineClass = { item: true }

            switch (this.item.status) {
                case 'suspended':
                    if (this.item.termination && !this.isSuspendedDueToUnverifiedEmail) {
                        lineClass.terminated = true
                    } else if (this.item.is_suspended_by_dunning || this.item.has_open_unverified_email_suspension) {
                        lineClass.suspended = true
                    }

                    return lineClass
                case 'terminated':
                    if (this.item.is_terminated_silently) {
                        lineClass['terminated-silently-contract'] = true
                    } else {
                        lineClass.terminated = true
                    }

                    return lineClass
                case 'terminated-contract':
                case 'cancelled':
                    lineClass['terminated-contract'] = true

                    return lineClass
                case 'order_pending':
                case 'wait_for_payment':
                case 'wait_for_fulfillment_start':
                case 'wait_for_fulfillment':
                case 'wait_for_input':
                case 'wait_for_activation':
                case 'after_payment':
                case 'active':
                default:
                    if (this.item.termination) {
                        lineClass.terminated = true
                    }
                    if (this.item.is_limited_runtime) {
                        lineClass['limited-runtime'] = true
                    }

                    return lineClass
            }
        },
        moneyTooltip () {
            const contractPhase = this.item.current_contract_phase
            let discount = '-'
            const type = contractPhase.discount_type === 'fixed'
                ? '€'
                : '%'
            if (contractPhase.discount_value) {
                discount = `${parseFloat(contractPhase.discount_value).toFixed(2)} ${type} ${contractPhase.discount_name}`
            }

            let finalTooltip = '<div style="text-align: left;">'

            finalTooltip += '<strong>' + this.__('contracts.list.tooltips.quantity') + '</strong> ' + this.quantity(this.item) + '<br />'
            finalTooltip += '<strong>' + this.__('contracts.list.tooltips.unit_price') + '</strong> ' + this.netUnitPrice(this.item) + ' / ' + this.grossUnitPrice(this.item) + '<br />'
            if (contractPhase.discount_value) finalTooltip += '<strong>' + this.__('contracts.list.tooltips.unit_price_discounted') + '</strong> ' + this.netPrice(this.item) + ' / ' + this.grossPrice(this.item) + '<br />'
            finalTooltip += '<br />'
            finalTooltip += '<strong>' + this.__('contracts.list.tooltips.total_price') + '</strong> ' + this.totalUndiscountedNetPrice(this.item) + ' / ' + this.totalUndiscountedGrossPrice(this.item) + '<br />'

            if (contractPhase.discount_value) {
                finalTooltip += '<br />'
                finalTooltip += '<strong>' + this.__('contracts.list.tooltips.discount') + '</strong> ' + discount + '<br />'
                finalTooltip += '<strong>' + this.__('contracts.list.tooltips.total_price_discounted') + '</strong> ' + this.totalNetPrice(this.item) + ' / ' + this.totalGrossPrice(this.item)
            }

            finalTooltip += '</div>'

            return finalTooltip
        },
        statusIcon () {
            if (this.item.active_termination_date) {
                return 'trash-can-outline'
            }

            switch (this.item.status) {
                case 'order_pending':
                    return 'account-clock-outline'
                case 'suspended':
                    return 'cancel'
                case 'wait_for_payment':
                case 'wait_for_fulfillment_start':
                case 'wait_for_fulfillment':
                case 'wait_for_input':
                case 'wait_for_activation':
                case 'wait_for_contract_start':
                case 'scheduled':
                    return 'timer-sand-empty'
                case 'after_payment':
                    return 'cash-multiple'
                case 'active':
                case 'done':
                    return 'check'
                case 'terminated':
                case 'terminated-contract':
                case 'cancelled':
                    return 'trash-can-outline'
                case 'draft':
                    return 'text-box-minus-outline'
                default:
                    return 'help'
            }
        },
        statusTooltip () {
            if (this.item.active_termination_date) {
                return this.__('customers.contracts.active_termination_date') + ' ' + this.item.active_termination_date + '<br />' +
                    this.__('customers.contracts.active_termination_created') + ' ' + this.item.active_termination_entry_date + '<br />' +
                    this.__('customers.contracts.active_termination_source') + ' ' + this.item.active_termination_source + '<br />' +
                    this.__('customers.contracts.active_termination_status') + ': ' + this.item.active_termination_status + '<br />' +
                    this.__('customers.contracts.active_termination_reason') + ': ' + this.item.active_termination_reason + '<br />' +
                    this.__('customers.contracts.active_termination_comment') + ': ' + this.item.active_termination_comment
            }

            if (this.item.status === 'suspended' && this.item.is_suspended_by_dunning) {
                return this.__('customers.contracts.suspension_reason_dunning')
            }

            return this.item.status
        },
        suspendReason () {
            if (this.item.status !== 'suspended') {
                return ''
            }

            if (this.item.is_suspended_by_dunning) {
                return this.__('customers.contracts.suspension_reason_dunning')
            }

            if (this.isSuspendedDueToUnverifiedEmail) {
                return this.__('customers.contracts.suspension_reason_unverified_email')
            }

            return this.__('customers.contracts.suspension_reason_termination')
        },
        terminatedReason () {
            if (this.item.status === 'terminated' && this.item.is_terminated_silently) {
                return this.__('customers.contracts.termination_reason_silent')
            }

            return ''
        },
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        invoiceNoteExcerpt () {
            if (typeof this.item.notes_invoice !== 'string') {
                return {
                    excerpt: '',
                    hasMore: false
                }
            }

            const excerpt = this.item.notes_invoice.split(' ', 50)
            const joinedExcerpt = excerpt.join(' ')

            return {
                excerpt: joinedExcerpt,
                hasMore: joinedExcerpt !== this.item.notes_invoice
            }
        },
        marginIndentation () {
            return this.isChild ? (this.depth + 1) * 8 : this.depth * 8
        },
        marginChildColor () {
            if (this.item.is_addon) {
                return 'var(--colorSecondary)'
            } else if (this.item.is_ff_child_contract) {
                return 'var(--colorPurple)'
            } else {
                return 'var(--colorPrimary)'
            }
        },
        connections () {
            return this.item.connections ?? []
        },
        isSuspendedDueToUnverifiedEmail () {
            // Contract can be suspended because of the unverified email (in which case does not have termination), but contract can also have termination by default when it is limited runtime
            return this.item.has_open_unverified_email_suspension && (this.item.termination === null || this.item.is_limited_runtime)
        }
    },
    methods: {
        connectionLabel (connection) {
            return connection.product_name
                ? `#${connection.id} - ${connection.product_name}`
                : `#${connection.id}`
        },
        focusConnection (connection) {
            const target = document.getElementById(`contract-${connection.id}`)

            if (target === null) {
                this.$inertia.get(this.$route('customers.customers.contracts.show', {
                    customer: connection.customer_id,
                    contract: connection.id
                }))

                return
            }

            target.scrollIntoView({ behavior: 'smooth', block: 'center' })
            target.classList.add('contract-connection-focused')
            window.setTimeout(() => target.classList.remove('contract-connection-focused'), FOCUS_HIGHLIGHT_MS)
        },
        navigateToSSO (item, type) {
            this.$emit('navigateToSSO', item, type)
        },
        showActivateContractModal (item) {
            this.$emit('clickActivate', item)
        },
        clickTerminate (item) {
            this.$emit('clickTerminate', item)
        },
        clickTerminateSilently (item) {
            this.$emit('clickTerminateSilently', item)
        },
        clickCancelTerminate (item) {
            this.$emit('clickCancelTerminate', item)
        },
        clickApplyMoneyBackGuarantee (item) {
            this.$emit('clickApplyMoneyBackGuarantee', item)
        },
        clickWithdrawal (item) {
            this.$emit('clickWithdrawal', item)
        },
        clickMoveContract (item) {
            this.$emit('clickMoveContract', item)
        },
        clickCancelContract (item) {
            this.$emit('clickCancelContract', item)
        },
        clickUnsuspend (item) {
            this.$emit('clickUnsuspend', item)
        },
        clickRelease (item) {
            this.$emit('clickRelease', item)
        },
        clickReleaseTree (item) {
            this.$emit('clickReleaseTree', item)
        },
        clickSendAuthCode (item) {
            this.$emit('clickSendAuthCode', item)
        },
        clickDelete (item) {
            this.$emit('clickDelete', item)
        },
        clickDeleteTree (item) {
            this.$emit('clickDeleteTree', item)
        },
        clickRestore (item) {
            this.$emit('clickRestore', item)
        },
        clickAddAddon (item) {
            this.$emit('clickAddAddon', item)
        },
        netPrice (item) {
            return this.priceFormat(item.current_contract_phase.discounted_price, 2, this.customer.market_region_id)
        },
        netUnitPrice (item) {
            return this.priceFormat(item.current_contract_phase.price, 2, this.customer.market_region_id)
        },
        grossUnitPrice (item) {
            return this.priceFormat(item.current_contract_phase.gross_undiscounted_price, 2, this.customer.market_region_id)
        },
        grossPrice (item) {
            return this.priceFormat(item.current_contract_phase.total_price, 2, this.customer.market_region_id)
        },
        totalNetPrice (item) {
            const totalNet = this.quantity(item) * parseFloat(item.current_contract_phase.discounted_price)

            return this.priceFormat(totalNet, 2, this.customer.market_region_id)
        },
        totalGrossPrice (item) {
            const totalGross = this.quantity(item) * parseFloat(item.current_contract_phase.total_price)

            return this.priceFormat(totalGross, 2, this.customer.market_region_id)
        },
        totalUndiscountedNetPrice (item) {
            const totalNet = this.quantity(item) * parseFloat(item.current_contract_phase.price)

            return this.priceFormat(totalNet, 2, this.customer.market_region_id)
        },
        totalUndiscountedGrossPrice (item) {
            const totalGross = this.quantity(item) * parseFloat(item.current_contract_phase.gross_undiscounted_price)

            return this.priceFormat(totalGross, 2, this.customer.market_region_id)
        },
        quantity (item) {
            return parseFloat(item.quantity)
        },
        showCustomerContractLink (contract) {
            return this.$route('customers.customers.contracts.show', {
                customer: contract.customer_id,
                contract: contract.id
            })
        },
        async setTooltipContents (contract) {
            const response = await window.axios.get(this.$route('customers.customers.contracts.technical-settings', {
                customer: contract.customer_id,
                contract: contract.id
            }))

            const tooltipInner = document.getElementsByClassName('loading-tooltip-body')

            if (tooltipInner.length > 0) {
                tooltipInner[0].innerHTML = this.getTooltipContent(contract, response.data.options, response.data.childOptions)
            }
        },
        getTooltipContent (contract, contractOptions, childOptions) {
            const options = {}

            for (const option of Object.values(contractOptions)) {
                if (option.is_product_setting === true) {
                    continue
                }

                options[option.name] = option
            }

            let tooltip = '<div style="text-align: left;"><strong>' + contract.product_name + '</strong></div>'

            tooltip += '<br>'

            if (Object.values(options).length === 0) {
                tooltip += '<div style="text-align: left;">' + this.__('customers.contracts.tooltip.no_items') + '</div>'
            }

            for (const item of Object.values(options)) {
                tooltip += '<div class="d-flex">'
                tooltip += '<div style="width: 150px; text-align: left;">' + item.translated_name + '</div>'
                if (item.display_value !== null) {
                    tooltip += '<div style="width: auto; text-align: left;">' + item.display_value + '</div>'
                } else {
                    if (item.value !== null) {
                        tooltip += '<div style="width: auto; text-align: left;">' + item.value + '</div>'
                    } else {
                        tooltip += '<div style="width: auto; text-align: left;"></div>'
                    }
                }
                tooltip += '</div>'
            }

            if (contract.fulfillment_module === 'ff_package') {
                const ffContracts = contract.ff_child_contracts

                for (const ffContract of ffContracts) {
                    const ffOptions = {}
                    tooltip += '<br>'

                    tooltip += '<div style="text-align: left;"><strong>' + ffContract.product_name + '</strong></div>'

                    tooltip += '<br>'

                    for (const option of Object.values(childOptions[ffContract.id])) {
                        if (option.is_product_setting === true) {
                            continue
                        }

                        ffOptions[option.name] = option
                    }

                    if (Object.values(ffOptions).length === 0) {
                        tooltip += '<div style="text-align: left;">' + this.__('customers.contracts.tooltip.no_items') + '</div>'
                    }

                    for (const item of Object.values(ffOptions)) {
                        tooltip += '<div class="d-flex">'
                        tooltip += '<div style="width: 150px; text-align: left;">' + item.translated_name + '</div>'
                        if (item.display_value !== null) {
                            tooltip += '<div style="width: auto; text-align: left;">' + item.value + ' ' + item.display_value + '</div>'
                        } else {
                            if (item.value !== null) {
                                tooltip += '<div style="width: auto; text-align: left;">' + item.value + '</div>'
                            } else {
                                tooltip += '<div style="width: auto; text-align: left;"></div>'
                            }
                        }
                        tooltip += '</div>'
                    }
                }
            }

            return tooltip
        }
    }
}
</script>

<style lang="scss" scoped>
.contract-list-item {
    background: var(--colorAppSurface);
    padding: 15px;
    &:not(:last-child) {
        border-bottom: 1px solid var(--colorAppBorder);
    }

    & .main {
        display: grid;
        grid-template-columns: 1fr auto auto;
        grid-gap: 15px;
        align-items: center;

        & .meta {
            display: grid;
            grid-template-columns: auto 1fr;
            grid-gap: 15px;
            align-items: center;

            & > div {
                display: grid;
                grid-gap: 5px;

                & span {
                    display: block;

                    &:nth-child(1) {
                        font-weight: bold;
                    }
                }
            }
        }
        & .actions {
            display: flex;
            grid-gap: 10px;

            & span {
                display: flex;
                grid-gap: 10px;
            }
        }
    }
    & .secondary {
        display: grid;
        grid-template-columns: repeat(4, 1fr) auto;
        grid-gap: 15px;
        align-items: center;

        & > div:not(.notes) {
            display: grid;
            grid-template-columns: auto 1fr;
            grid-gap: 15px;
            align-items: center;
            line-height: 1.5em;
        }
        & > .connections > div {
            display: grid;
            grid-gap: 8px;
            min-width: 0;

            & > div {
                display: flex;
                align-items: center;
                grid-gap: 5px;
                min-width: 0;
                line-height: 1.5;

                & :deep(.d-tag) {
                    flex-shrink: 0;
                }
                & a {
                    color: inherit;
                    min-width: 0;
                    overflow: hidden;
                    white-space: nowrap;
                    text-overflow: ellipsis;

                    &:hover {
                        text-decoration: underline;
                    }
                }
            }
        }
        & > .invoice-note {
            grid-column: 1 / span 5;
            display: grid;
            grid-template-columns: auto 1fr;
            grid-gap: 15px;
            align-items: center;
            line-height: 1.5em;
        }
    }
    & .middle {
        display: grid;
        margin-top: 5px;
    }
    & .children {
        display: grid;
    }
}
.terminated {
    background: var(--colorDangerLight);
}
.terminated-contract {
    background: var(--colorDangerDark);
}
.suspended {
    background: var(--colorPrimaryLight);
}
.limited-runtime {
    background: var(--colorAppNonProductionLight);
}
.terminated-silently-contract {
    background: var(--colorWarningLight);
}
.pointer-events {
    pointer-events: auto !important;
}
.invoice-note-modal-text {
    padding: 15px;
    line-height: 1.5em;
}
.contracts-list-contract-filter-exists {
    border: 1px solid rgb(255 0 0 / 50%) !important;
}
.contract-connection-focused {
    box-shadow: inset 0 0 0 2px var(--colorPrimary);
}
</style>

<style lang="scss">
.tooltip-inner {max-width: unset !important}
</style>
